/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withNavigation } from 'src/ui/hoc';
import { Button, Column, MuiTitle, Container, Field } from 'src/ui/mui-ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Field as FormField, FormSpy } from 'react-final-form';
import propTypes from 'prop-types';

import { UiInputAdapter, UiToastAdapter, LoginImage } from 'src/ui';
import { composeValidators, required, email } from 'src/ui/validations';
import { toast } from 'react-toastify';
import { signIn, clearAuthErrors, getToken } from '../index.js';
// Import action types directly from the actions file
import { SIGN_IN_SUCCESS, SIGN_IN_FAILURE } from '../redux/actions';
import { ResetPasswordModal } from '../components/index.js';

class Login extends Component {
  state = {
    resettingPassword: false,
    currentEmail: '',
    isNexaFlow: window.location.host.includes('nexaflow'),
    isDatasym: window.location.host.split('.')[0].includes('datasym'),
    isTerra:
      window.location.host.split('.')[0].includes('terra') ||
      window.location.hostname === 'localhost',
    isSubmitting: false, // Add this new state variable
  };

  componentDidMount() {
    const { clearAuthErrors } = this.props;
    clearAuthErrors();
  }

  componentDidUpdate(prevProps) {
    const { token, history } = this.props;
    const { currentEmail } = this.state;

    // Add detailed debugging
    // console.log('LoginPage.componentDidUpdate', {
    //   prevLoading: prevProps.token.isLoading(),
    //   currentLoading: token.isLoading(),
    //   tokenData: token.data,
    //   currentEmail,
    // });

    if (prevProps.token.isLoading() && !token.isLoading()) {
      // console.log('Token finished loading, checking for 2FA requirement...');

      // Check if we received a 2FA required response
      if (
        token.data &&
        (token.data.code === '2fa_required' || token.data.is2faRequired)
      ) {
        // console.log('2FA required detected in componentDidUpdate, redirecting');

        // Use direct navigation
        window.location.href = `/two-factor-auth?email=${encodeURIComponent(
          token.data.email || currentEmail
        )}`;
        return;
      }

      // Normal authentication flow
      if (token.data && token.data.access) {
        // console.log('Login successful, redirecting to account selection');
        history.push('/account-selection');
      } else {
        // Show any error messages
        const errorMessage = token.errorMessage ? token.errorMessage() : null;
        if (errorMessage) {
          // console.log('Login error:', errorMessage);
          toast(UiToastAdapter(errorMessage, 'danger'));
        }
      }
    }
  }

  handleSubmit = values => {
    const { signIn } = this.props;
    const { email, password } = values;

    // Set submitting state to true to disable the button
    this.setState({ currentEmail: email, isSubmitting: true });

    // console.log('Submitting login for:', email);

    // Dispatch the sign in action
    signIn({
      email,
      password,
    })
      .then(action => {
        // console.log('Sign in action response:', action);

        // Safely check the response type and payload
        if (action && action.type) {
          // Handle success case
          if (action.type === SIGN_IN_SUCCESS) {
            // console.log('Sign in success action:', action.payload);

            // Check if this is a 2FA response
            if (
              action.payload &&
              (action.payload.code === '2fa_required' ||
                action.payload.is2faRequired)
            ) {
              // console.log('2FA required detected in SUCCESS action');

              // Important: Force navigation outside of React Router to avoid auth checks
              setTimeout(() => {
                const emailToUse = action.payload.email || email;
                // console.log('Redirecting to 2FA page with email:', emailToUse);
                window.location.href = `/two-factor-auth?email=${encodeURIComponent(
                  emailToUse
                )}`;
              }, 100);
            }
          }
          // Handle failure case
          else if (action.type === SIGN_IN_FAILURE) {
            // console.log('Sign in failure action:', action.payload);

            // Check if this is a 2FA response masquerading as an error
            if (action.payload && action.payload.code === '2fa_required') {
              // console.log('2FA required detected in FAILURE action');

              setTimeout(() => {
                // console.log(
                //   'Redirecting to 2FA page with email from failure:',
                //   email
                // );
                window.location.href = `/two-factor-auth?email=${encodeURIComponent(
                  email
                )}`;
              }, 100);
            } else {
              // If not a 2FA response, show error and re-enable the button
              const errorMessage =
                action.payload?.detail ||
                action.payload?.message ||
                'Invalid credentials. Please try again.';
              toast(UiToastAdapter(errorMessage, 'danger'));
              this.setState({ isSubmitting: false });
            }
          }
        } else {
          console.error('Invalid action response:', action);
          toast(
            UiToastAdapter('An unexpected error occurred. Please try again.', 'danger')
          );
          // Re-enable the button on error
          this.setState({ isSubmitting: false });
        }
      })
      .catch(error => {
        console.error('Error in sign in action:', error);
        toast(
          UiToastAdapter('An unexpected error occurred. Please try again.', 'danger')
        );
        // Re-enable the button on error
        this.setState({ isSubmitting: false });
      });
  };

  onToggleResetPassword = resettingPassword => {
    this.setState({ resettingPassword });
  };

  render() {
    const {
      resettingPassword,
      currentEmail,
      isNexaFlow,
      isTerra,
      isDatasym,
      isSubmitting, // Add this to destructuring
    } = this.state;

    return (
      <>
        <Container className="login">
          <Column>
            {/* {!isWhiteLabel ? <SolventLogo /> : null} */}

            <MuiTitle isSize={4} tag="h2" hasTextColor="secondary">
              Login
            </MuiTitle>

            <p className="has-text-grey-medium-dark is-size-7">
              Please login to access your account.
            </p>

            <Form
              onSubmit={this.handleSubmit}
              render={({ handleSubmit, submitting, hasValidationErrors }) => (
                <>
                  <FormSpy
                    subscription={{
                      values: {
                        email: true,
                      },
                    }}
                    onChange={({ values: { email } }) => {
                      this.setState({ currentEmail: email });
                    }}
                  />
                  <form
                    name="form"
                    onSubmit={handleSubmit}
                    className="login-form"
                  >
                    <FormField
                      name="email"
                      component={UiInputAdapter}
                      validate={composeValidators('Email', required, email)}
                      label="Email"
                      type="email"
                      id="email"
                      hasIcons="left"
                      className="login-input"
                      renderIcons={() => [
                        <span className="icon is-left is-small" key="envelope">
                          <FontAwesomeIcon
                            icon={['fal', 'envelope']}
                            size="sm"
                          />
                        </span>,
                      ]}
                      disabled={submitting}
                      autoFocus
                      required
                      hideAsterisk
                    />

                    <FormField
                      name="password"
                      component={UiInputAdapter}
                      validate={required('Password')}
                      label="Password"
                      type="password"
                      id="password"
                      className="login-input"
                      hasIcons="right"
                      renderIcons={() => [
                        <span className="icon is-right is-small" key="lock">
                          <FontAwesomeIcon
                            icon={['fal', 'lock-alt']}
                            size="sm"
                          />
                        </span>,
                      ]}
                      disabled={submitting}
                      required
                      hideAsterisk
                    />

                    <Field>
                      <Button
                        isColor="gradient-bright"
                        className="is-long"
                        disabled={
                          submitting || hasValidationErrors || isSubmitting
                        } // Add isSubmitting here
                        isLoading={submitting || isSubmitting} // Also show loading state
                        type="submit"
                      >
                        Login
                      </Button>
                    </Field>
                  </form>
                </>
              )}
            />

            <Button
              className="login-help-link is-paddingless"
              isColor="text"
              onClick={() => this.onToggleResetPassword(true)}
            >
              Need help logging in?
            </Button>
          </Column>
        </Container>

        {isTerra && (
          <LoginImage
            image={require('../../ui/assets/LoginImageGeneric.png')}
          />
        )}

        <ResetPasswordModal
          isActive={resettingPassword}
          currentEmail={currentEmail}
          onCancel={() => this.onToggleResetPassword(false)}
        />
      </>
    );
  }
}

Login.propTypes = {
  history: propTypes.object.isRequired,
  token: propTypes.object.isRequired,
  signIn: propTypes.func.isRequired,
  clearAuthErrors: propTypes.func.isRequired,
};

const mapStateToProps = state => ({
  token: getToken(state),
});

export const LoginPage = connect(mapStateToProps, { signIn, clearAuthErrors })(
  withNavigation(Login)
);
